import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import useSpeechDialogStore from './speech-dialog.store.ts'
import useAudioStore from '@/shared/store/audio.store.tsx'
import { formatSecondsToMinutesSeconds } from '@/utils/formatters.ts'
import Icon from '@/components/common/Icon.tsx'
import Spinner from '@/components/common/Spinner.tsx'
import { useSpeechStore } from '@/shared/store/speech.store.ts'
import type { InputSpeechData } from '@/features/session/domain/models/session.ts'
import type { Voice } from '@/features/voice/domain/models/voice.ts'

const Progress: FC<{ trackingId?: string }> = ({ trackingId }) => {
  const { progress, currentAudioId } = useAudioStore(
    useShallow(({ progress, currentAudioId }) => ({
      progress,
      currentAudioId,
    }))
  )
  const currentProgress = currentAudioId === trackingId ? progress * 100 : 0
  return (
    <progress
      value={Math.round(currentProgress)}
      max={100}
      className="overflow-hidden rounded-full"
    />
  )
}

const PlayButton: FC<{
  isPlaying?: boolean
  isLoading?: boolean
  onClick: () => void
}> = ({ onClick, isLoading, isPlaying }) => {
  const isIdle = !isLoading && !isPlaying
  const classes = [
    'flex h-[40px] w-[40px] items-center justify-center rounded-full text-white bg-zinc-600',
    isLoading && 'bg-transparent',
    isPlaying && 'bg-zinc-600',
  ]
  return (
    <button
      type="button"
      disabled={isLoading}
      onClick={onClick}
      className={classes.join(' ')}
    >
      {isIdle && <Icon name="play" />}
      {isLoading && <Spinner className="h-8 w-8" />}
      {isPlaying && <Icon name="pause" />}
    </button>
  )
}

const SpeechPreview: FC = () => {
  const { t } = useTranslation()
  const {
    fetchVoices,
    expressive,
    selectedVoice,
    scriptText,
    selectedAudioFileId,
    audioFiles,
    validateScriptText,
    validateAudioFiles,
    speed,
    selectedTab,
  } = useSpeechDialogStore()
  const audioFile = audioFiles.find((file) => file.id === selectedAudioFileId)
  const [isLoading, setIsLoading] = useState(false)
  const { regeneratePreview, preview } = useSpeechStore()
  const mode = selectedTab === 'text' ? 'tts' : 'sts'
  const speechCompare: InputSpeechData = {
    type: mode,
    scriptText,
    fileId: audioFile?.mediaFile?.id,
    voice: selectedVoice as Voice,
    expressive,
    speed,
  }

  useEffect(() => {
    if (selectedVoice) return
    void fetchVoices()
  }, [fetchVoices, selectedVoice])

  const {
    playAudioBuffer,
    stopPlayback,
    isAudioPlaying,
    duration,
    currentAudioId,
  } = useAudioStore(
    useShallow(
      ({
        isPlaying,
        playAudioBuffer,
        stopPlayback,
        duration,
        currentAudioId,
      }) => ({
        stopPlayback,
        playAudioBuffer,
        currentAudioId,
        isAudioPlaying: isPlaying,
        duration,
      })
    )
  )

  const isPlaying = isAudioPlaying && preview?.id === currentAudioId
  const previewCallback = async () => {
    if (!validateScriptText(scriptText) || !selectedVoice) {
      return
    }
    if (mode === 'sts' && !validateAudioFiles()) return // STS requires a file id
    const currentPreview = preview ?? (await regeneratePreview(speechCompare))
    void playAudioBuffer(
      currentPreview.id,
      currentPreview.buffer as AudioBuffer
    )
  }
  const onPreview = async () => {
    setIsLoading(true)
    await previewCallback()
    setIsLoading(false)
  }

  const onStop = () => {
    stopPlayback()
  }

  return (
    <div className="flex w-full max-w-[543px] flex-row items-center gap-[12px]">
      <PlayButton
        isPlaying={isPlaying}
        isLoading={isLoading}
        onClick={isPlaying ? onStop : onPreview}
      />
      <div className="flex flex-1 flex-col gap-[8px]">
        <div className="flex flex-row justify-between text-xs font-semibold">
          <span>{t('ls_generic_preview')}</span>
          <span>{formatSecondsToMinutesSeconds(duration)}</span>
        </div>
        {preview ? <Progress trackingId={preview.id} /> : <Progress />}
      </div>
    </div>
  )
}

export default SpeechPreview
